import { useEffect, useState } from "react";
import { api, ROLE_LABELS, SECTION_LABELS, visibleSections, formatDateTime } from "@/lib/api";
import { CalendarDays, Plus, MapPin, Trash2, Pencil, Download, Globe, Copy, Rss, MessageCircle, CalendarPlus, Check, X, HelpCircle, Users2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ALL_SECTIONS = ["tutti", "consiglio", "fondatori", "soci", "collaboratori"];

// Ordina: prima le riunioni future/da svolgere (dalla più vicina), poi in fondo quelle passate o svolte (dalla più recente)
function sortMeetings(list) {
  const now = Date.now();
  const isDone = (m) => !!m.completed || new Date(m.end || m.start).getTime() < now;
  return [...list].sort((a, b) => {
    const da = isDone(a), db = isDone(b);
    if (da !== db) return da ? 1 : -1;
    const sa = new Date(a.start).getTime(), sb = new Date(b.start).getTime();
    return da ? sb - sa : sa - sb;
  });
}

function relTime(startIso, endIso, completed) {
  const now = new Date();
  if (completed) return { t: "Svolta", c: "bg-[#DCFCE7] text-[#166534]" };
  const s = new Date(startIso);
  const end = new Date(endIso || startIso);
  if (end.getTime() < now.getTime()) return { t: "Passata", c: "bg-[#EDEAE3] text-[#6B6558]" };
  if (s.getTime() <= now.getTime()) return { t: "In corso", c: "bg-[#FDE8E7] text-[#EF3E36]" };
  const day = 86400000;
  const sot = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const smd = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
  const d = Math.round((smd - sot) / day);
  if (d === 0) return { t: "Oggi", c: "bg-[#FDE8E7] text-[#EF3E36]" };
  if (d === 1) return { t: "Domani", c: "bg-[#FEF3C7] text-[#92400E]" };
  if (d < 7) return { t: `Tra ${d} giorni`, c: "bg-[#FEF3C7] text-[#92400E]" };
  if (d < 14) return { t: "Tra 1 settimana", c: "bg-[#F0EADF] text-[#6B6558]" };
  if (d < 60) return { t: `Tra ${Math.round(d / 7)} settimane`, c: "bg-[#F0EADF] text-[#6B6558]" };
  return { t: `Tra ${Math.round(d / 30)} mesi`, c: "bg-[#F0EADF] text-[#6B6558]" };
}

export default function CalendarSection({ user, isAdmin }) {
  const [tab, setTab] = useState("tutti");
  const [meetings, setMeetings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showWP, setShowWP] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [hideDone, setHideDone] = useState(false);
  const SECTIONS = ALL_SECTIONS.filter(s => visibleSections(user).includes(s));
  const [waLink, setWaLink] = useState(null);
  const [assocCal, setAssocCal] = useState({ google_calendar_add_url: "", google_calendar_id: "" });
  const [form, setForm] = useState({ title: "", description: "", section: "tutti", start: "", end: "", location: "" });
  const backend = process.env.REACT_APP_BACKEND_URL;
  const icsFeed = `${backend}/api/public/events.ics`;
  const icsFeedSection = tab && tab !== "tutti" ? `${icsFeed}?section=${tab}` : icsFeed;
  const jsonFeed = `${backend}/api/public/events.json`;
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato negli appunti"); };

  const load = () => {
    const q = tab === "tutti" ? "" : `?section=${tab}`;
    api.get(`/meetings${q}`).then(r => setMeetings(r.data));
    api.get("/whatsapp/status").then(r => setWaLink(r.data.community_invite_url)).catch(() => {});
    api.get("/config").then(r => setAssocCal({
      google_calendar_add_url: r.data.google_calendar_add_url || "",
      google_calendar_id: r.data.google_calendar_id || "",
    })).catch(() => {});
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start || !form.end) { toast.error("Compila tutti i campi obbligatori"); return; }
    try {
      const body = { ...form, start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() };
      if (editingId) {
        await api.put(`/meetings/${editingId}`, body);
        toast.success("Riunione aggiornata");
      } else {
        await api.post("/meetings", body);
        toast.success("Riunione creata");
      }
      setShowForm(false); setEditingId(null);
      setForm({ title: "", description: "", section: "tutti", start: "", end: "", location: "" });
      load();
    } catch { toast.error("Errore salvataggio riunione"); }
  };

  const toggleComplete = async (m) => {
    try { await api.patch(`/meetings/${m.id}/completed`, { completed: !m.completed }); load(); }
    catch { toast.error("Errore aggiornamento stato"); }
  };

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };
  const edit = (m) => {
    setForm({ title: m.title || "", description: m.description || "", section: m.section || "tutti", start: toLocalInput(m.start), end: toLocalInput(m.end), location: m.location || "" });    setEditingId(m.id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa riunione? Verrà rimossa anche dal Calendario Google condiviso.")) return;
    try { await api.delete(`/meetings/${id}`); toast.success("Riunione eliminata (anche da Google Calendar)"); load(); }
    catch { toast.error("Errore durante l'eliminazione"); }
  };

  const sync = async (m) => {
    try {
      const r = await api.post(`/meetings/${m.id}/sync`);
      if (r.data.ok) toast.success(r.data.action === "created" ? "Riunione aggiunta a Google Calendar" : "Google Calendar aggiornato");
      else if (r.data.configured === false) toast.error("Google Calendar non è ancora configurato (Impostazioni → Google Calendar)");
      else toast.error("Sincronizzazione non riuscita, riprova");
      load();
    } catch { toast.error("Errore di sincronizzazione"); }
  };

  const googleCalLink = (m) => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    // NB: lo slash tra le date deve restare letterale (URLSearchParams lo codifica in %2F e Google non legge le date)
    return "https://calendar.google.com/calendar/render?action=TEMPLATE"
      + `&text=${encodeURIComponent(m.title || "")}`
      + `&dates=${fmt(m.start)}/${fmt(m.end)}`
      + `&details=${encodeURIComponent(m.description || "")}`
      + `&location=${encodeURIComponent(m.location || "")}`;
  };

  const whatsAppReminder = (m) => {
    const when = new Date(m.start).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const loc = m.location ? `\n📍 ${m.location}` : "";
    const desc = m.description ? `\n\n${m.description}` : "";
    const text = `📅 *Promemoria Riunione* — ${SECTION_LABELS[m.section] || m.section}\n\n*${m.title}*\n🗓️ ${when}${loc}${desc}\n\n➡️ Info sulla piattaforma: https://app.amunicassibile.it/dashboard?tab=calendar`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  return (
    <div data-testid="section-calendar">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><CalendarDays className="w-3 h-3" /> Riunioni</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Calendario</h1>
          <p className="text-[#6B6558] mt-2">Riunioni divise per sezione. Aggiungile a Google Calendar con un click.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {assocCal.google_calendar_add_url && (
            <a
              data-testid="subscribe-shared-gcal"
              href={assocCal.google_calendar_add_url}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]"
              title="Aggiungi il Calendario Amunì al tuo account Google: vedrai tutte le riunioni direttamente su Google Calendar"
            >
              <CalendarPlus className="w-4 h-4" /> Iscriviti al Calendario Amunì
            </a>
          )}
          <a
            data-testid="subscribe-ics"
            href={icsFeed.replace(/^https?:\/\//, "webcal://")}
            className="px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4]"
            title="Sottoscrivi il feed — funziona con Apple Calendar (iPhone/Mac), Outlook, Thunderbird"
          >
            <Rss className="w-4 h-4 text-[#2B3A33]" /> iPhone / Apple / Outlook
          </a>
          <button data-testid="gcal-help-btn" onClick={() => setShowHelp(v => !v)} className="px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4]">
            <HelpCircle className="w-4 h-4 text-[#2B3A33]" /> Non funziona?
          </button>
          <button data-testid="wp-connect-btn" onClick={() => setShowWP(v => !v)} className="px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4]">
            <Globe className="w-4 h-4 text-[#2B3A33]" /> Collega WordPress
          </button>
          {isAdmin && (
            <button data-testid="new-meeting-btn" onClick={() => { setEditingId(null); setForm({ title: "", description: "", section: "tutti", start: "", end: "", location: "" }); setShowForm(v => !v); }} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
              <Plus className="w-4 h-4" /> Nuova Riunione
            </button>
          )}
        </div>
      </div>

      {showHelp && (
        <div className="paper rounded-2xl p-6 mb-6 space-y-3 text-sm text-[#3C4A44]" data-testid="gcal-help-panel">
          <div className="font-display text-xl font-bold text-[#2B3A33]">Come aggiungere il Calendario Amunì</div>
          <div>
            <div className="font-semibold text-[#2B3A33]">Google Calendar (PC o telefono)</div>
            <ol className="list-decimal list-inside space-y-1 mt-1">
              <li>Apri <a className="underline" href="https://calendar.google.com" target="_blank" rel="noreferrer">calendar.google.com</a> dal browser (non dall'app).</li>
              <li>A sinistra, accanto ad <b>Altri calendari</b> tocca <b>+</b> → <b>Iscriviti al calendario</b>.</li>
              <li>Incolla questo ID e premi Invio:</li>
            </ol>
            <div className="mt-2 flex gap-2 items-center">
              <code data-testid="gcal-id-code" className="flex-1 text-xs bg-white border border-[#E8E2D8] rounded-lg px-3 py-2 break-all">{assocCal.google_calendar_id}</code>
              <button data-testid="gcal-copy-id" onClick={() => { navigator.clipboard.writeText(assocCal.google_calendar_id); toast.success("ID copiato"); }} className="px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white text-xs font-semibold">Copia</button>
            </div>
            <p className="text-xs text-[#6B6558] mt-1">Una volta aggiunto sul PC, compare automaticamente anche nell'app Google Calendar del telefono.</p>
          </div>
          <div>
            <div className="font-semibold text-[#2B3A33]">iPhone / iPad (Calendario Apple)</div>
            <p className="mt-1">Tocca <b>iPhone / Apple / Outlook</b> qui sopra e conferma <b>Iscriviti</b>. Oppure: Impostazioni → Calendario → Account → Aggiungi account → Altro → Aggiungi calendario con sottoscrizione → incolla <code className="text-xs">{icsFeed}</code>.</p>
          </div>
        </div>
      )}

      {!assocCal.google_calendar_add_url && isAdmin && (
        <div className="paper rounded-2xl p-4 mb-6 bg-[#FEF3C7] border-l-4 border-[#D97706] text-sm text-[#78350F]" data-testid="gcal-not-configured">
          <strong>⚠️ Calendario Google non configurato.</strong> Vai in <b>Impostazioni → Google Calendar</b> per collegare il calendario condiviso: senza questo, i soci non possono iscriversi con un click.
        </div>
      )}

      {showWP && (
        <div className="paper rounded-2xl p-6 mb-6 space-y-4" data-testid="wp-panel" style={{background: "linear-gradient(180deg, #F3EEE4, #FFFFFF)"}}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2B3A33] text-white flex items-center justify-center shrink-0">
              <Rss className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-xl font-bold text-[#2B3A33]">Collega al tuo sito WordPress</div>
              <p className="text-sm text-[#6B6558] mt-1">
                Gli eventi vengono sincronizzati in automatico via feed .ics standard. Nessuna doppia gestione: crei una riunione qui, appare sul sito.
              </p>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold mb-2">Feed ICS pubblico {tab !== "tutti" && `(sezione: ${tab})`}</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input readOnly value={icsFeedSection} data-testid="ics-feed-url" className="flex-1 px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white font-mono text-xs" />
              <button data-testid="copy-ics" onClick={() => copy(icsFeedSection)} className="terracotta-btn px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 justify-center">
                <Copy className="w-4 h-4" /> Copia
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold mb-2">Feed JSON (integrazioni custom)</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input readOnly value={jsonFeed} data-testid="json-feed-url" className="flex-1 px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white font-mono text-xs" />
              <button data-testid="copy-json" onClick={() => copy(jsonFeed)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4] inline-flex items-center gap-2 justify-center">
                <Copy className="w-4 h-4" /> Copia
              </button>
            </div>
          </div>

          <div className="text-sm text-[#2B3A33] bg-white border border-[#E8E2D8] rounded-xl p-4 space-y-2">
            <div className="font-semibold">Come collegarlo in 2 minuti:</div>
            <ol className="list-decimal ml-5 space-y-1 text-[#6B6558]">
              <li>Nel tuo WordPress installa il plugin gratuito <b>ICS Calendar</b> (o <b>The Events Calendar</b> se lo usi già).</li>
              <li>Aggiungi un nuovo calendario e incolla l&apos;URL del feed .ics qui sopra.</li>
              <li>Imposta l&apos;aggiornamento automatico (es. ogni 5 minuti) — fatto!</li>
            </ol>
            <div className="text-xs text-[#8A8272] pt-1">Suggerimento: per mostrare solo le riunioni pubbliche seleziona la tab <b>Tutti</b>; per una pagina soci usa la sezione specifica.</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map(s => (
          <button
            key={s}
            data-testid={`cal-tab-${s}`}
            onClick={() => setTab(s)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${tab === s ? "bg-[#2B3A33] text-white" : "bg-white border border-[#E8E2D8] text-[#2B3A33] hover:bg-[#F3EEE4]"}`}
          >
            {SECTION_LABELS[s]}
          </button>
        ))}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="meeting-form">
          <input required data-testid="meeting-title" placeholder="Titolo" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <textarea data-testid="meeting-desc" placeholder="Descrizione" rows={2} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required data-testid="meeting-start" type="datetime-local" value={form.start} onChange={e => setForm(f => ({...f, start: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <input required data-testid="meeting-end" type="datetime-local" value={form.end} onChange={e => setForm(f => ({...f, end: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="meeting-location" placeholder="Luogo" value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <select data-testid="meeting-section" value={form.section} onChange={e => setForm(f => ({...f, section: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {ALL_SECTIONS.map(s => <option key={s} value={s}>{SECTION_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="meeting-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold">{editingId ? "Salva modifiche" : "Crea"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      {meetings.length > 0 && (
        <div className="flex items-center justify-end mb-3">
          <button data-testid="toggle-hide-done" onClick={() => setHideDone(v => !v)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B3A33]">
            <span className={`w-9 h-5 rounded-full transition relative ${hideDone ? "bg-[#EF3E36]" : "bg-[#D8D0C2]"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${hideDone ? "left-[18px]" : "left-0.5"}`} />
            </span>
            Nascondi svolte
          </button>
        </div>
      )}
      {(() => {
        const sorted = sortMeetings(meetings);
        const now = Date.now();
        const done = (m) => !!m.completed || new Date(m.end || m.start).getTime() < now;
        const upcoming = sorted.filter(m => !done(m));
        const past = sorted.filter(done);
        const card = (m, featured = false) => (
          <MeetingCard key={m.id} m={m} featured={featured} isAdmin={isAdmin} onDelete={del} onEdit={edit} onSync={sync} onToggleComplete={toggleComplete} googleCalLink={googleCalLink} whatsAppReminder={whatsAppReminder} backend={backend} />
        );
        if (meetings.length === 0) {
          return (
            <div className="paper p-10 rounded-2xl text-center">
              <CalendarDays className="w-10 h-10 mx-auto text-[#C9BEA8] mb-3" />
              <div className="font-display text-lg font-bold text-[#2B3A33]">Nessuna riunione in programma</div>
              <p className="text-sm text-[#8A8272] mt-1">Le prossime riunioni di questa sezione appariranno qui.</p>
            </div>
          );
        }
        return (
          <div className="space-y-3">
            {upcoming.length > 0 && (
              <div className="flex items-center gap-3 pt-1" data-testid="group-upcoming">
                <span className="text-xs uppercase tracking-widest font-bold text-[#EF3E36] inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Prossime riunioni</span>
                <span className="text-[11px] font-bold text-[#EF3E36] bg-[#FDF2F2] rounded-full px-2 py-0.5">{upcoming.length}</span>
                <span className="h-px flex-1 bg-[#E8E2D8]" />
              </div>
            )}
            {upcoming.map((m, i) => card(m, i === 0))}
            {!hideDone && past.length > 0 && (
              <div className="flex items-center gap-3 pt-5" data-testid="group-past">
                <span className="text-xs uppercase tracking-widest font-bold text-[#8A8272] inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> Svolte e passate</span>
                <span className="text-[11px] font-bold text-[#8A8272] bg-[#EDEAE3] rounded-full px-2 py-0.5">{past.length}</span>
                <span className="h-px flex-1 bg-[#E8E2D8]" />
              </div>
            )}
            {!hideDone && past.map(m => card(m))}
            {hideDone && past.length > 0 && upcoming.length === 0 && (
              <div className="paper p-6 rounded-2xl text-center text-sm text-[#8A8272]">Nessuna riunione in programma. {past.length} svolte/passate nascoste.</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function MeetingCard({ m, featured, isAdmin, onDelete, onEdit, onSync, onToggleComplete, googleCalLink, whatsAppReminder, backend }) {
  const [rsvp, setRsvp] = useState({ attendees_yes: [], count_yes: 0, count_maybe: 0, count_no: 0, my_rsvp: null });
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadRsvp = () => api.get(`/meetings/${m.id}/rsvps`).then(r => setRsvp(r.data)).catch(() => {});
  useEffect(() => { loadRsvp(); /* eslint-disable-next-line */ }, [m.id]);
  const setStatus = async (status) => {
    setLoading(true);
    try {
      await api.post(`/meetings/${m.id}/rsvp`, { status });
      toast.success(status === "yes" ? "Parteciperai!" : status === "maybe" ? "Segnato come forse" : "Segnato come non parteciperai");
      loadRsvp();
    } catch { toast.error("Errore RSVP"); }
    finally { setLoading(false); }
  };
  const btnClass = (kind, active) => `px-2.5 py-1 rounded-lg text-xs font-semibold inline-flex items-center gap-1 border transition ${active ? kind === "yes" ? "bg-[#16A34A] text-white border-[#16A34A]" : kind === "maybe" ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "bg-[#EF3E36] text-white border-[#EF3E36]" : "bg-white border-[#E8E2D8] text-[#3C4A44] hover:bg-[#F3EEE4]"}`;
  return (
    <div data-testid={`meeting-${m.id}`} className={`paper rounded-2xl p-5 card-hover ${m.completed ? "opacity-60" : ""} ${featured && !m.completed ? "ring-2 ring-[#EF3E36] ring-offset-2 ring-offset-[#FAF7F2]" : ""}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`shrink-0 text-center rounded-xl px-4 py-3 min-w-[84px] ${m.completed ? "bg-[#EDEAE3]" : "bg-[#FDF2F2]"}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#8A8272]">
            {new Date(m.start).toLocaleDateString("it-IT", { weekday: "short" })}
          </div>
          <div className={`font-display text-3xl font-bold leading-none ${m.completed ? "text-[#8A8272]" : "text-[#2B3A33]"}`}>
            {new Date(m.start).getDate()}
          </div>
          <div className={`text-xs uppercase tracking-widest font-bold ${m.completed ? "text-[#8A8272]" : "text-[#EF3E36]"}`}>
            {new Date(m.start).toLocaleDateString("it-IT", { month: "short" })}
          </div>
          <div className="text-[10px] text-[#8A8272] mt-0.5">
            {new Date(m.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {featured && !m.completed && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#2B3A33] text-white">Prossima</span>}
            <h3 className={`font-display text-lg font-bold ${m.completed ? "line-through text-[#8A8272]" : "text-[#2B3A33]"}`}>{m.title}</h3>
            {!m.completed && (() => { const r = relTime(m.start, m.end, m.completed); return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${r.c}`}>{r.t}</span>; })()}
            <span className={`role-${m.section} text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase`}>{SECTION_LABELS[m.section] || m.section}</span>
            {m.completed && <span data-testid={`done-badge-${m.id}`} className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-[#DCFCE7] text-[#166534] inline-flex items-center gap-1"><Check className="w-3 h-3" /> Svolto</span>}
            {m.google_event_id && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#E0F2FE] text-[#075985] font-semibold uppercase" title="Sincronizzato su Google Calendar">GCal</span>}
          </div>
          <div className="text-sm text-[#6B6558] mt-1">{formatDateTime(m.start)} — {formatDateTime(m.end)}</div>
          {m.location && <div className="text-xs text-[#8A8272] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</div>}
          {m.description && <div className="text-sm text-[#6B6558] mt-2">{m.description}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <a data-testid={`wa-${m.id}`} href={whatsAppReminder(m)} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-[#16A34A] text-white text-xs font-semibold hover:bg-[#15803D] inline-flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </a>
          )}
          <a data-testid={`gcal-${m.id}`} href={googleCalLink(m)} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-[#1A73E8] text-white text-xs font-semibold hover:bg-[#1558B0] inline-flex items-center gap-1" title="Salva solo questa riunione nel tuo Google Calendar">
            <CalendarPlus className="w-3 h-3" /> Aggiungi a Google Calendar
          </a>
          <a data-testid={`ics-${m.id}`} href={`${backend}/api/meetings/${m.id}/ics`} className="px-3 py-1.5 rounded-lg bg-white border border-[#E8E2D8] text-xs font-semibold hover:bg-[#F3EEE4] inline-flex items-center gap-1">
            <Download className="w-3 h-3" /> .ics
          </a>
          {isAdmin && (
            <button data-testid={`complete-meeting-${m.id}`} onClick={() => onToggleComplete(m)} title={m.completed ? "Segna come da svolgere" : "Segna come svolto"} className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1 ${m.completed ? "border-[#16A34A] text-[#16A34A] bg-[#F0FDF4]" : "border-[#E8E2D8] text-[#2B3A33] hover:bg-[#F3EEE4]"}`}>
              <Check className="w-3.5 h-3.5" /> {m.completed ? "Svolto" : "Svolgi"}
            </button>
          )}
          {isAdmin && (
            <button data-testid={`sync-meeting-${m.id}`} onClick={() => onSync(m)} title="Forza la sincronizzazione su Google Calendar (se non compare)" className="px-2.5 py-1.5 rounded-lg border border-[#1A73E8] text-[#1A73E8] bg-[#EFF6FF] text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#DBEAFE]">
              <RefreshCw className="w-3.5 h-3.5" /> Sincronizza
            </button>
          )}
          {isAdmin && (
            <button data-testid={`edit-meeting-${m.id}`} onClick={() => onEdit(m)} title="Modifica" className="p-1.5 rounded-lg border border-[#E8E2D8] text-[#2B3A33] hover:bg-[#F3EEE4]">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {isAdmin && (
            <button data-testid={`del-meeting-${m.id}`} onClick={() => onDelete(m.id)} title="Elimina riunione (anche da Google Calendar)" className="px-2.5 py-1.5 rounded-lg border border-[#EF3E36] text-[#EF3E36] bg-[#FDF2F2] text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#FCE4E4]">
              <Trash2 className="w-3.5 h-3.5" /> Elimina
            </button>
          )}
        </div>
      </div>

      {/* RSVP block */}
      <div className="mt-4 border-t border-[#E8E2D8] pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold mr-2">Parteciperai?</span>
          <button disabled={loading} data-testid={`rsvp-yes-${m.id}`} onClick={() => setStatus("yes")} className={btnClass("yes", rsvp.my_rsvp === "yes")}>
            <Check className="w-3 h-3" /> Sì
          </button>
          <button disabled={loading} data-testid={`rsvp-maybe-${m.id}`} onClick={() => setStatus("maybe")} className={btnClass("maybe", rsvp.my_rsvp === "maybe")}>
            <HelpCircle className="w-3 h-3" /> Forse
          </button>
          <button disabled={loading} data-testid={`rsvp-no-${m.id}`} onClick={() => setStatus("no")} className={btnClass("no", rsvp.my_rsvp === "no")}>
            <X className="w-3 h-3" /> No
          </button>
          <button
            data-testid={`rsvp-toggle-${m.id}`}
            onClick={() => setExpanded(v => !v)}
            className="ml-auto text-xs font-semibold text-[#2B3A33] hover:underline inline-flex items-center gap-1"
          >
            <Users2 className="w-3.5 h-3.5" />
            {rsvp.count_yes} {rsvp.count_yes === 1 ? "socio parteciperà" : "soci parteciperanno"}
            {rsvp.count_maybe > 0 && <span className="text-[#8A8272]"> · {rsvp.count_maybe} forse</span>}
          </button>
        </div>
        {expanded && rsvp.attendees_yes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`attendees-${m.id}`}>
            {rsvp.attendees_yes.map(a => (
              <span key={a.email} className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${a.source === "google" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#DCFCE7] text-[#166534]"}`}>
                <Check className="w-3 h-3" />
                {a.name}
                {a.source === "google" && <span className="text-[9px] opacity-70 uppercase">GCal</span>}
              </span>
            ))}
          </div>
        )}
        {expanded && rsvp.attendees_yes.length === 0 && (
          <div className="mt-2 text-xs text-[#8A8272] italic">Nessuno ha ancora confermato la presenza.</div>
        )}
      </div>
    </div>
  );
}
